import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, BellRing, Check, Loader2, Sparkles } from "lucide-react";
import { Chapter, Reveal } from "@/components/Chapter";
import { CourseCards } from "@/components/CourseCards";
import { useContent } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-400/30 transition-colors";

export const AcademyWaitlist = () => {
  const { ACADEMY } = useContent();
  const [form, setForm] = useState({ name: "", email: "", interest: ACADEMY.interests[0] });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const formRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickCourse = (title) => {
    setForm((f) => ({ ...f, interest: title }));
    setDone(null);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    toast(`Great choice — "${title}" selected. Add your details below.`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/academy/waitlist`, form);
      if (data.status === "already_joined") {
        toast.info("You're already on the list — we'll email you at launch.");
      } else {
        toast.success("You're on the list! Check your inbox for a welcome note.");
      }
      setDone(form.name.split(" ")[0]);
    } catch {
      toast.error("Couldn't add you right now. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="academy" className="relative py-24 md:py-32 overflow-hidden" data-testid="academy-section">
      <div className="glow-amber-light absolute -top-20 -right-32 h-[520px] w-[520px] rounded-full pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        <Chapter id="05" name="Desantrix Academy" title={ACADEMY.title} sub={ACADEMY.sub} />

        <CourseCards onPick={pickCourse} />

        <div ref={formRef} className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-[#0B1528] shadow-[0_24px_50px_-20px_rgba(11,21,40,0.5)] mb-8"
              >
                <GraduationCap className="h-9 w-9 text-amber-400" />
              </motion.div>
              <span className="absolute top-0 left-24 inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-400/40 px-3 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-amber-700">
                <Sparkles className="h-3 w-3" /> {ACADEMY.eyebrow}
              </span>
              <ul className="space-y-4" data-testid="academy-perks">
                {ACADEMY.perks.map((p, i) => (
                  <motion.li
                    key={p}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                    className="flex items-center gap-3 text-base text-slate-700"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400/15 border border-amber-400/40">
                      <Check className="h-3.5 w-3.5 text-amber-600" />
                    </span>
                    {p}
                  </motion.li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <motion.div
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="relative rounded-3xl border border-amber-300/70 bg-gradient-to-br from-amber-50 via-white to-blue-50 p-8 md:p-10 shadow-[0_30px_70px_-30px_rgba(255,159,28,0.4)]"
              data-testid="academy-waitlist-card"
            >
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8" data-testid="academy-success">
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.1 }}
                      className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 border border-emerald-300"
                    >
                      <Check className="h-8 w-8 text-emerald-600" />
                    </motion.span>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0B1528]">You're on the list, {done}.</h3>
                    <p className="mt-3 text-sm text-slate-500 max-w-sm mx-auto">We'll write to you the moment enrolment opens. Keep an eye on your inbox for a welcome note from Desantrix Academy.</p>
                  </motion.div>
                ) : (
                  <motion.form key="form" exit={{ opacity: 0, scale: 0.97 }} onSubmit={submit} className="space-y-5" data-testid="academy-waitlist-form">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-amber-300 shadow-sm">
                        <BellRing className="h-5 w-5 text-amber-500 animate-ring" />
                      </span>
                      <div>
                        <h3 className="text-lg font-bold text-[#0B1528]">Notify me at launch</h3>
                        <p className="text-xs text-slate-500">Free to join. Leave anytime.</p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="academy-name" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Name *</label>
                        <input id="academy-name" data-testid="academy-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="academy-email" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Email *</label>
                        <input id="academy-email" data-testid="academy-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="academy-interest" className="mb-2 block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">I'm most interested in</label>
                      <select id="academy-interest" data-testid="academy-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
                        {ACADEMY.interests.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="academy-submit-button"
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#0B1528] px-8 py-4 text-base font-semibold text-white hover:bg-amber-500 hover:text-[#0B1528] active:scale-95 disabled:opacity-60 transition-all duration-300"
                    >
                      {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <BellRing className="h-5 w-5" />}
                      {sending ? "Adding you..." : "Join the waiting list"}
                    </button>
                    <p className="text-[11px] text-slate-400">By joining you agree to receive launch updates from Desantrix Academy. No spam, ever.</p>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
